import type { ReactElement } from 'react'
import { useRef } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import { handleEscapeKeyDown } from '../../../../../utils/hooks/useSortableHelpers'
import { useTranslate } from '../../../../../utils/translate'
import Accordion from '../components/Accordion/Accordion'
import Button from '../../../../formComponents/Button'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './ExpanderSettings'))

export default function ExpanderPlugin({
  data = Immutable.fromJS({ entries: [] }),
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const t = useTranslate('interface', 'components.expanderElementComponent')
  const ref = useRef<HTMLDivElement>(null)

  const pluginActiveClasses = cc([
    'dali-plugin-expander',
    {
      'dali-grid-element-highlighted': editorMode === 'edit',
    },
  ])

  const accordionData = data.get('entries').map((entry) => ({
    title: entry.get('title'),
    content: entry.get('detailsHtml'),
  }))

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {data.get('entries') && data.get('entries').some((entry) => entry.get('title') || entry.get('detailsHtml')) ? (
          data.get('entries').map((entry, index) => (
            <details key={index}>
              <summary>{entry.get('title')}</summary>
              <div dangerouslySetInnerHTML={{ __html: entry.get('detailsHtml') }}></div>
            </details>
          ))
        ) : (
          <div className={'dali-grid-element-placeholder'}>
            <span className="dali-grid-element-placeholder-icon dali-plugin-expander-placeholder-icon" />
            <Button type="button" onClick={() => onEdit()}>
              {t('addTopicsButton.label')}
            </Button>
          </div>
        )}
        <SettingsLayer
          open={editorMode === 'edit'}
          className="dali-settingslayer-expander"
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={handleEscapeKeyDown(onCancel)}
        >
          {({ renderLayout }) => <SettingsForm {...{ data, onDataChange, onSave, onCancel, renderLayout }} />}
        </SettingsLayer>
      </div>
    )
  }

  return (
    <div className={pluginActiveClasses}>
      <Accordion data={accordionData} innerTextClassName={'dali-plugin-expander-text'} />
    </div>
  )
}
